import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ArrowRight, BookOpen, ListChecks, MessageSquare, Briefcase, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InlineSpinner } from "@/components/common/Loading";
import GuideFlipbook, { FullViewer } from "./GuideFlipbook";
import { WhyAcquire, StartStrong, GrowthIncentives } from "./OpportunitySections";
import { Qualify, Timeline, FindingAgency } from "./QualifySections";
import TransitionSection from "./TransitionSection";
import { Readiness, NextStep, DistrictTeam } from "./ProgressSections";
import BonusCalculator from "./BonusCalculator";
import OpportunityCard from "./OpportunityCard";
import { Faq, Materials } from "./MaterialsSections";
import { SectionHead } from "./acqUi";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

function Hero({ d, onGuide, onTalk }) {
  const c = d.content, p = d.program;
  return (
    <section className="relative rounded-2xl overflow-hidden bg-[#0B2E4F] text-white" data-testid="acq-hero">
      <img src={c.hero_image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" data-testid="acq-hero-image" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B2E4F] via-[#0B2E4F]/90 to-[#0B2E4F]/40" />
      <div className="relative p-6 sm:p-10 lg:p-12 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-end">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-sky-300" data-testid="acq-hero-eyebrow">{c.hero_eyebrow}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] mt-3 max-w-2xl" data-testid="acq-hero-headline">{c.hero_headline}</h1>
          <p className="mt-4 text-base sm:text-lg text-white/85 max-w-xl" data-testid="acq-hero-copy">{c.hero_copy}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button onClick={onTalk} className="bg-white text-[#0B2E4F] hover:bg-sky-50 h-11 px-5" data-testid="acq-cta-talk"><MessageSquare className="w-4 h-4 mr-2" />{c.cta.talk}</Button>
            <Button onClick={onGuide} variant="outline" className="bg-transparent border-white/60 text-white hover:bg-white/10 hover:text-white h-11 px-5" data-testid="acq-cta-guide"><BookOpen className="w-4 h-4 mr-2" />{c.cta.guide}</Button>
            <Button onClick={() => scrollTo("acq-requirements")} variant="ghost" className="text-white hover:bg-white/10 hover:text-white h-11" data-testid="acq-cta-requirements"><ListChecks className="w-4 h-4 mr-2" />{c.cta.requirements}</Button>
            {p.has_opportunity && <Button onClick={() => scrollTo("acq-opportunity")} variant="ghost" className="text-white hover:bg-white/10 hover:text-white h-11" data-testid="acq-cta-opportunity"><Briefcase className="w-4 h-4 mr-2" />{c.cta.opportunity}</Button>}
          </div>
        </div>
        <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/15 p-5" data-testid="acq-program-card">
          <p className="text-[11px] uppercase tracking-wider text-white/60">Your Program</p>
          <p className="font-semibold text-lg" data-testid="acq-program-name">{p.name}</p>
          <p className="text-sm text-white/75">{p.version_label} · Official Guide: {p.guide_label}</p>
          <div className="mt-4 flex items-end justify-between gap-3">
            <div><p className="text-[11px] uppercase tracking-wider text-white/60">Current Phase</p><p className="font-semibold" data-testid="acq-current-phase">{p.current_phase || "Getting Started"}</p></div>
            <p className="font-display text-3xl font-bold" data-testid="acq-hero-progress">{p.progress}%</p>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-white/15 overflow-hidden"><div className="h-full bg-sky-300 rounded-full" style={{ width: `${p.progress}%` }} /></div>
        </div>
      </div>
    </section>
  );
}

export default function AcquisitionJourney({ goTo }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [page, setPage] = useState(1);
  const [full, setFull] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  useEffect(() => { api.get("/acquisition/journey").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <p className="text-sm text-slate-500" data-testid="acq-unavailable">The Acquisition Journey is available to External Acquisition candidates.</p>;
  if (!d) return <div className="py-16 flex justify-center"><InlineSpinner /></div>;
  const c = d.content, o = d.official, s = c.sections;
  const talk = () => (goTo ? goTo("support", "team") : window.dispatchEvent(new Event("tr-open-support")));
  const openGuide = (n = page) => { setViewDoc(d.guide); setPage(n); setFull(true); };
  const toCalc = () => scrollTo("acq-calculator");
  const viewerDoc = viewDoc || d.guide;
  return (
    <div className="space-y-14 sm:space-y-16 pb-6" data-testid="acq-journey">
      <Hero d={d} onGuide={() => scrollTo("acq-guide")} onTalk={talk} />
      {c.intro && <p className="text-base sm:text-lg text-slate-700 max-w-3xl leading-relaxed -mt-4" data-testid="acq-intro">{c.intro}</p>}
      <section id="acq-guide" data-testid="acq-guide-section">
        <SectionHead eyebrow="Program Guide" title={`${o.guide_title} · ${o.guide_label}`} text={s.guide}
          right={<p className="text-xs text-slate-500 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" />Authorized access only</p>} />
        <GuideFlipbook doc={d.guide} toc={o.toc} page={page} setPage={setPage} onOpenFull={openGuide} />
      </section>
      <WhyAcquire items={o.why} text={s.why} />
      <StartStrong items={o.startup} note={o.startup_note} text={s.startup} />
      <GrowthIncentives items={o.incentives} note={o.incentive_note} text={s.incentives} onCalc={toCalc} />
      <Qualify items={d.qualifications} text={s.qualify} goTo={goTo} />
      <Timeline steps={o.timeline} note={o.timeline_note} text={s.timeline} />
      <FindingAgency text={s.finding} image="/acquisition/handshake.jpg" />
      <TransitionSection text={s.transition} dates={d.dates} hasDates={d.has_dates} onRules={() => openGuide(o.commission_rules_page)} />
      <div id="acq-readiness" className="space-y-6">
        <OpportunityCard opp={d.opportunity} />
        <Readiness items={d.readiness} text={s.readiness} progress={d.program.progress} goTo={goTo} />
        <NextStep step={d.next_step} goTo={goTo} />
      </div>
      <BonusCalculator text={s.calculator} />
      <DistrictTeam people={d.support} message={c.support_message} cta={c.cta.contact} onContact={talk} />
      <Faq items={c.faq} />
      <Materials docs={d.documents} text={s.materials} onView={(doc) => { setViewDoc(doc); setPage(1); setFull(true); }} onCalc={toCalc} />
      <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="text-sm text-[#006090] inline-flex items-center gap-1" data-testid="acq-back-top">Back to top <ArrowRight className="w-3.5 h-3.5 -rotate-90" /></button>
      <FullViewer doc={viewerDoc} toc={viewerDoc?.kind === "guide" ? o.toc : []} open={full} onOpenChange={setFull} page={page} setPage={setPage} />
    </div>
  );
}
